/**
 * Ground clamp — keep a posed model's world-space bounding box above the road.
 *
 * Used by the crash cinematic: the sim's crash cyclist is a single Rapier
 * ROUNDED BOX (~0.76 x 1.44 x 0.68 m; see the CYC_* block in sim/world.ts)
 * whose centre can never go below its own half-extent, but the VISIBLE cyclist
 * GLB is longer and wider than that box. Posing the model from the body's
 * centre + orientation
 * therefore digs its extremities (handlebars, wheels, the rider's limbs) into
 * the asphalt during the tumble, and again in the settled lying pose.
 *
 * The fix is purely visual and costs one affine box-min evaluation per frame:
 * take the model's LOCAL bbox (measured once at load), find the minimum world
 * Y of that box under the model's current world matrix, and lift the model by
 * whatever amount that min-Y sits below the road.
 *
 * Both functions are pure and allocation-free.
 */

/** An axis-aligned box in some local frame. */
export interface BoxMinMax {
  min: { x: number; y: number; z: number };
  max: { x: number; y: number; z: number };
}

/**
 * Minimum world Y of an axis-aligned box after an affine transform.
 *
 * For an affine matrix the transformed Y of a point is
 *   y' = m[1]·x + m[5]·y + m[9]·z + m[13]
 * (three.js Matrix4 `elements` are column-major, so those four entries are row
 * 1). Each term is independent and monotonic in its own coordinate, so the
 * minimum over the box is the sum of the per-axis minima — exact, and cheaper
 * than transforming all 8 corners.
 *
 * @param box local-space bounding box.
 * @param e   column-major 16-element affine matrix (Matrix4.elements).
 */
export function transformedBoxMinY(box: BoxMinMax, e: ArrayLike<number>): number {
  const { min, max } = box;
  return (
    e[13] +
    Math.min(e[1] * min.x, e[1] * max.x) +
    Math.min(e[5] * min.y, e[5] * max.y) +
    Math.min(e[9] * min.z, e[9] * max.z)
  );
}

/**
 * How far to push an object UP so its lowest point clears the ground.
 *
 * Returns 0 when the object is already clear (this never pulls anything down —
 * a model resting well above the road is left where the physics put it).
 *
 * @param minY    lowest world Y of the object (see transformedBoxMinY).
 * @param groundY world Y of the road surface (default 0).
 * @param epsilon extra clearance so the model never z-fights the asphalt.
 */
export function groundLiftY(minY: number, groundY = 0, epsilon = 0.01): number {
  const target = groundY + epsilon;
  return minY < target ? target - minY : 0;
}
